import {
  CSSProperties,
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import sprite from '../../../assets/icons/sprite.svg';
import { ChannelContext, ConfigContext } from '../../../context';
import { TabItem, Tabs, useIsMyTopicsAllowed, useIsPersonalizationEnabled } from '../../../shared';
import { NewsFeedMyTopicsModal } from '../NewsFeedMyTopicsModal';

import classes from './NewsFeedSubTabs.module.scss';

export const NewsFeedSubTabs: FunctionComponent = memo(() => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { id: channelId } = useContext(ChannelContext).channel;

  const { newsFeed } = useContext(ConfigContext).config.elements;

  const isPersonalizationEnabled = useIsPersonalizationEnabled();

  const isMyTopicsAllowed = useIsMyTopicsAllowed();

  const [isMyTopicsOpen, setIsMyTopicsOpen] = useState(false);

  const subTabs = newsFeed?.subTabs;

  const subTabsItems = useMemo(
    () => subTabs?.channels?.find(({ channelId: id }) => id === channelId)?.items,
    [channelId, subTabs]
  );

  const selectedStoryId = useMemo(
    () => Number(searchParams.get('storyId')) || undefined,
    [searchParams]
  );

  const onSelectStoryId = useCallback(
    (storyId?: number) => {
      if (!storyId) {
        navigate({ search: '' }, { replace: true });
        return;
      }
      navigate({ search: `?storyId=${storyId}` }, { replace: true });
    },
    [navigate]
  );

  const customizeFeedAction = useMemo(
    () => (
      <button
        type={'button'}
        className={classes['news-feed-sub-tabs__customize']}
        aria-label={t('newsFeed.customize-feed')}
        aria-haspopup={'dialog'}
        onClick={() => setIsMyTopicsOpen(true)}
      >
        <svg className={classes['news-feed-sub-tabs__customize-icon']} aria-hidden>
          <use href={`${sprite}#down-arrow`} />
        </svg>
      </button>
    ),
    [t]
  );

  const tabItems = useMemo((): TabItem[] => {
    if (!isPersonalizationEnabled && !subTabsItems?.length) {
      return [];
    }

    return [
      {
        index: 0,
        name: t(isPersonalizationEnabled ? 'newsFeed.your-feed' : 'common.all'),
        ...(isMyTopicsAllowed && { action: customizeFeedAction }),
      },
      ...(subTabsItems ?? []).map(({ titleLocaliseKey }, i) => ({
        index: i + 1,
        name: t(titleLocaliseKey),
      })),
    ];
  }, [customizeFeedAction, isMyTopicsAllowed, isPersonalizationEnabled, subTabsItems, t]);

  const selectedTabIndex = useMemo(() => {
    const storyTabIndex =
      subTabsItems?.findIndex(({ storyId }) => storyId === selectedStoryId) ?? -1;

    return storyTabIndex >= 0 ? storyTabIndex + 1 : 0;
  }, [selectedStoryId, subTabsItems]);

  const onTabSelect = useCallback(
    (index: number) => {
      if (isMyTopicsAllowed && index === 0 && selectedTabIndex === 0) {
        setIsMyTopicsOpen(true);
        return;
      }

      onSelectStoryId(index === 0 ? undefined : subTabsItems?.[index - 1]?.storyId);
    },
    [isMyTopicsAllowed, onSelectStoryId, selectedTabIndex, subTabsItems]
  );

  const closeHandler = useCallback(() => setIsMyTopicsOpen(false), []);

  if (!tabItems.length) {
    return null;
  }

  return (
    <>
      <Tabs
        items={tabItems}
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={onTabSelect}
        tabNamesStyle={
          {
            '--tabs-label-color': subTabs?.color,
            '--tabs-active-color': subTabs?.hoverColor,
          } as CSSProperties
        }
        scrollable
        noBackground
      />

      <NewsFeedMyTopicsModal isOpen={isMyTopicsOpen} onClose={closeHandler} />
    </>
  );
});
